import { Badge, Button, formatDuration, formatPrice, formatTime, shortName } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { SALON_ID, SALON_TIMEZONE as TZ, api } from "../lib/trpc";

type ClientChoice =
  | { mode: "existing"; id: string; label: string; allergies: string[] | null }
  | { mode: "new"; firstName: string; lastName: string; phone: string };

/**
 * Rendez-vous existant ouvert en modification. Volontairement structurel :
 * la forme exacte vient de `agenda.day`, on n'en reprend que ce qui se saisit.
 */
export type EditableAppointment = {
  id: string;
  staffId: string;
  blockStartsAt: Date;
  staffNote: string | null;
  guestName: string | null;
  client: {
    id: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
    clientProfile: { allergies: string[] | null } | null;
  } | null;
  lines: { serviceId: string | null }[];
};

/**
 * Saisie d'un rendez-vous au comptoir — création ET modification.
 *
 * Le parcours cliente et celui de l'équipe n'ont pas les mêmes contraintes :
 * une cliente choisit parmi des créneaux proposés, l'équipe pose un rendez-vous
 * où elle veut — y compris hors des disponibilités en ligne, pour une cliente
 * qui pousse la porte.
 *
 * Une chose ne se contourne pas : le chevauchement. La contrainte base tranche,
 * et le message revient ici.
 */
/**
 * Cliente déjà connue au moment d'ouvrir le formulaire — cas du hub, où l'on
 * crée un rendez-vous DEPUIS une conversation. Sans ça, l'équipe retapait le
 * nom d'une cliente que l'écran affichait déjà à côté.
 */
export type PrefilledClient = {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  allergies?: string[] | null;
};

export function AppointmentForm({
  salonId,
  staff,
  defaultStaffId,
  defaultStartsAt,
  defaultClient,
  conversationId,
  appointment,
  onClose,
}: {
  salonId: string;
  staff: { id: string; displayName: string; calendarColor: string }[];
  defaultStaffId?: string;
  defaultStartsAt: Date;
  /** Création pré-remplie : la cliente est déjà identifiée par l'appelant. */
  defaultClient?: PrefilledClient;
  /**
   * Ouvert depuis une conversation : débloque « Proposer ce rendez-vous »,
   * qui pose le créneau à confirmer ET envoie la proposition dans le fil.
   */
  conversationId?: string;
  /** Absent : création. Présent : modification de ce rendez-vous. */
  appointment?: EditableAppointment;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = appointment != null;

  const [staffId, setStaffId] = useState(
    appointment?.staffId ?? defaultStaffId ?? staff[0]?.id ?? "",
  );
  const [serviceIds, setServiceIds] = useState<string[]>(
    () => appointment?.lines.map((line) => line.serviceId).filter((id): id is string => id != null) ?? [],
  );
  const [startsAt, setStartsAt] = useState(appointment?.blockStartsAt ?? defaultStartsAt);
  const [search, setSearch] = useState("");
  const [client, setClient] = useState<ClientChoice | null>(
    () => initialClient(appointment) ?? prefilledClient(defaultClient),
  );
  const [note, setNote] = useState(appointment?.staffNote ?? "");
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  /**
   * `null` = le texte suit encore le créneau choisi. Dès que l'équipe tape,
   * on cesse de l'écraser : une phrase retouchée ne doit pas disparaître
   * parce qu'on a décalé l'heure de dix minutes.
   */
  const [proposalMessage, setProposalMessage] = useState<string | null>(null);

  const services = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api.admin.catalog.services.query({ salonId: SALON_ID }),
  });

  const candidates = useQuery({
    queryKey: ["clientSearch", search],
    queryFn: () => api.agenda.searchClients.query({ salonId: SALON_ID, query: search }),
    enabled: search.trim().length >= 2 && client === null,
  });

  const selected = useMemo(
    () => (services.data ?? []).filter((service) => serviceIds.includes(service.id)),
    [services.data, serviceIds],
  );

  const totals = useMemo(
    () => ({
      minutes: selected.reduce((sum, service) => sum + service.durationMinutes, 0),
      cents: selected.reduce((sum, service) => sum + service.priceCents, 0),
    }),
    [selected],
  );

  /**
   * Le créneau est-il libre ? Purement informatif pour la saisie au comptoir —
   * une cliente qui pousse la porte se sert quand même — mais l'équipe doit
   * voir qu'elle sort du cadre avant de valider, pas après.
   */
  const slot = useQuery({
    queryKey: ["checkSlot", staffId, serviceIds.join(","), startsAt.toISOString()],
    queryFn: () =>
      api.agenda.checkSlot.query({
        salonId: SALON_ID,
        staffId,
        serviceIds,
        blockStartsAt: startsAt,
      }),
    enabled: Boolean(staffId) && serviceIds.length > 0,
  });

  /** Proposition par défaut : la phrase que l'équipe écrirait de toute façon. */
  const suggestion = useMemo(() => {
    const jour = new Intl.DateTimeFormat("fr-FR", {
      timeZone: TZ,
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(startsAt);
    const prestations = selected.map((service) => service.name.toLowerCase()).join(" + ");
    const prenom = client?.mode === "existing" ? client.label.split(" ")[0] : "";
    return `Bonjour ${prenom} ! Je vous propose ${jour} à ${formatTime(startsAt, TZ)}${
      prestations ? ` pour ${prestations}` : ""
    }. Est-ce que cela vous convient ?`;
  }, [startsAt, selected, client]);

  const propose = useMutation({
    mutationFn: () =>
      api.agenda.proposeAppointment.mutate({
        salonId: SALON_ID,
        conversationId: conversationId!,
        staffId,
        serviceIds,
        blockStartsAt: startsAt,
        clientId: client?.mode === "existing" ? client.id : undefined,
        newClient:
          client?.mode === "new"
            ? {
                firstName: client.firstName,
                lastName: client.lastName || undefined,
                phone: client.phone || undefined,
              }
            : undefined,
        staffNote: note || undefined,
        message: proposalMessage ?? suggestion,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread"] });
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
      refreshAndClose();
    },
  });

  /** Après écriture, tout ce qui peint une date doit repartir du serveur. */
  const refreshAndClose = () => {
    queryClient.invalidateQueries({ queryKey: ["planning"] });
    queryClient.invalidateQueries({ queryKey: ["pulse"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
    onClose();
  };

  const save = useMutation({
    mutationFn: () => {
      const clientInput = {
        clientId: client?.mode === "existing" ? client.id : undefined,
        newClient:
          client?.mode === "new"
            ? {
                firstName: client.firstName,
                lastName: client.lastName || undefined,
                phone: client.phone || undefined,
              }
            : undefined,
      };

      if (editing) {
        return api.agenda.updateAppointment.mutate({
          appointmentId: appointment.id,
          staffId,
          serviceIds,
          blockStartsAt: startsAt,
          ...clientInput,
          staffNote: note || null,
        });
      }

      return api.agenda.createAppointment.mutate({
        salonId,
        staffId,
        serviceIds,
        blockStartsAt: startsAt,
        ...clientInput,
        staffNote: note || undefined,
      });
    },
    onSuccess: refreshAndClose,
  });

  const cancel = useMutation({
    mutationFn: () => api.agenda.cancelAppointment.mutate({ appointmentId: appointment!.id }),
    onSuccess: refreshAndClose,
  });

  const pending = save.isPending || cancel.isPending;
  const error = save.error ?? cancel.error;
  const ready = Boolean(staffId) && serviceIds.length > 0 && client !== null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
      className="fixed inset-0 z-50 flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-lg overflow-auto rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-2xl">{editing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>

        {/* --- 1. La cliente ---------------------------------------------- */}
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-encre-700">Cliente</legend>

          {client ? (
            <div className="flex items-center gap-3 rounded-lg bg-creme-100 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-encre-800">
                {client.mode === "existing"
                  ? client.label
                  : `${client.firstName} ${client.lastName}`.trim()}
              </span>
              {client.mode === "existing" && client.allergies?.length ? (
                <Badge tone="danger">⚠️ {client.allergies.join(", ")}</Badge>
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => setClient(null)}>
                Changer
              </Button>
            </div>
          ) : (
            <>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher : nom ou téléphone…"
                className="h-11 w-full rounded-lg border border-creme-300 px-3"
              />

              {candidates.data && candidates.data.length > 0 && (
                <ul className="mt-1 divide-y divide-creme-200 rounded-lg border border-creme-200">
                  {candidates.data.map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        onClick={() =>
                          setClient({
                            mode: "existing",
                            id: candidate.id,
                            label: `${shortName(candidate.firstName, candidate.lastName)} · ${candidate.phone ?? "—"}`,
                            allergies: candidate.allergies,
                          })
                        }
                        className="w-full px-3 py-2 text-left text-sm hover:bg-creme-100"
                      >
                        {shortName(candidate.firstName, candidate.lastName)}
                        <span className="text-encre-400">
                          {" "}
                          · {candidate.phone ?? "—"} · {candidate.visitCount ?? 0} visite(s)
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Aucune correspondance : le dire, sinon le champ a l'air cassé. */}
              {search.trim().length >= 2 &&
                candidates.data &&
                candidates.data.length === 0 && (
                  <p className="mt-1 px-1 text-sm text-encre-400">
                    Aucune fiche ne correspond.
                  </p>
                )}

              {/* Création à la volée : une cliente qui pousse la porte ne doit
                  pas obliger à quitter l'écran pour créer sa fiche. */}
              {search.trim().length >= 2 && (
                <button
                  type="button"
                  onClick={() => {
                    // « Juliette Martin » se decoupe : tout mettre dans le
                    // prenom produisait des fiches au nom bancal, et cassait
                    // la recherche par nom de famille.
                    const [first, ...rest] = search.trim().split(/\s+/);
                    setClient({
                      mode: "new",
                      firstName: first ?? "",
                      lastName: rest.join(" "),
                      phone: "",
                    });
                  }}
                  className="mt-2 text-sm font-medium text-blush-600 underline underline-offset-4"
                >
                  Créer « {search.trim()} » comme nouvelle cliente
                </button>
              )}
            </>
          )}

          {client?.mode === "new" && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <input
                value={client.firstName}
                onChange={(event) => setClient({ ...client, firstName: event.target.value })}
                placeholder="Prénom"
                className="h-10 rounded-lg border border-creme-300 px-3"
              />
              <input
                value={client.lastName}
                onChange={(event) => setClient({ ...client, lastName: event.target.value })}
                placeholder="Nom (facultatif)"
                className="h-10 rounded-lg border border-creme-300 px-3"
              />
              <input
                value={client.phone}
                onChange={(event) => setClient({ ...client, phone: event.target.value })}
                placeholder="Téléphone"
                inputMode="tel"
                className="h-10 rounded-lg border border-creme-300 px-3 sm:col-span-2"
              />
            </div>
          )}
        </fieldset>

        {/* --- 2. Prestations --------------------------------------------- */}
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-encre-700">Prestations</legend>
          <div className="flex flex-wrap gap-1.5">
            {services.data
              ?.filter((service) => service.isActive || serviceIds.includes(service.id))
              .map((service) => {
                const on = serviceIds.includes(service.id);
                return (
                  <button
                    key={service.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setServiceIds((current) =>
                        on
                          ? current.filter((id) => id !== service.id)
                          : [...current, service.id],
                      )
                    }
                    className={[
                      "rounded-full px-3 py-1.5 text-sm transition-colors",
                      on ? "bg-blush-500 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
                    ].join(" ")}
                  >
                    {service.name}
                  </button>
                );
              })}
          </div>
          {selected.length > 0 && (
            <p className="mt-2 text-sm text-encre-500">
              {formatDuration(totals.minutes)} · {formatPrice(totals.cents)}
            </p>
          )}
        </fieldset>

        {/* --- 3. Quand et avec qui --------------------------------------- */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-medium text-encre-700">Employée</span>
            <select
              value={staffId}
              onChange={(event) => setStaffId(event.target.value)}
              className="h-11 w-full rounded-lg border border-creme-300 bg-white px-3"
            >
              {staff.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-1 block text-sm font-medium text-encre-700">Début</span>
            <input
              type="datetime-local"
              value={toLocalInput(startsAt)}
              onChange={(event) => setStartsAt(fromLocalInput(event.target.value))}
              className="h-11 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
            />
          </label>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-encre-700">
            Note interne (facultatif)
          </span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Prévoir la dépose, base à retirer…"
            className="h-11 w-full rounded-lg border border-creme-300 px-3"
          />
        </label>

        {/*
          Créneau hors cadre : on prévient sans interdire. La saisie au comptoir
          doit rester capable d'accepter l'exception — mais en connaissance de
          cause. La proposition à distance, elle, sera refusée par le serveur.
        */}
        {slot.data && !slot.data.available && (
          <p className="mb-3 rounded-md bg-ambre-100 px-3 py-2 text-sm text-ambre-700">
            ⚠ Créneau hors disponibilité : {slot.data.issues.join(" · ")}.
            {conversationId && " Une proposition ne peut pas partir sur ce créneau."}
          </p>
        )}

        {error && (
          <p role="alert" className="mb-3 rounded-md bg-ambre-100 px-3 py-2 text-sm font-medium text-ambre-700">
            {error instanceof Error
              ? error.message
              : "Le rendez-vous n'a pas pu être enregistré."}
          </p>
        )}

        {propose.error && (
          <p role="alert" className="mb-3 rounded-md bg-grenat-100 px-3 py-2 text-sm font-medium text-grenat-700">
            {propose.error instanceof Error
              ? propose.error.message
              : "La proposition n'a pas pu être envoyée."}
          </p>
        )}

        {/* --- Proposition depuis la conversation -------------------------- */}
        {conversationId && !editing && (
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium text-encre-700">
              Message de proposition
            </span>
            <textarea
              value={proposalMessage ?? suggestion}
              onChange={(event) => setProposalMessage(event.target.value)}
              rows={3}
              className="w-full rounded-lg border border-creme-300 px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-encre-400">
              Relisez avant d'envoyer : ce texte part tel quel à la cliente.
            </span>
          </label>
        )}

        <Button block size="lg" loading={save.isPending} disabled={!ready || pending} onClick={() => save.mutate()}>
          {editing ? "Enregistrer" : "Créer le rendez-vous"}
        </Button>

        {/*
          Deuxième action, volontairement sous la première : créer un rendez-vous
          est une décision du salon, le proposer est une question posée à la
          cliente. Le rendez-vous est posé « à confirmer », ce qui réserve le
          créneau pendant qu'elle réfléchit — sans quoi la même heure pourrait
          être promise deux fois.
        */}
        {conversationId && !editing && (
          <Button
            block
            variant="secondary"
            className="mt-2"
            loading={propose.isPending}
            disabled={!ready || pending || propose.isPending || slot.data?.available === false}
            onClick={() => propose.mutate()}
          >
            Proposer ce rendez-vous
          </Button>
        )}

        {/*
          L'annulation vit sous l'action principale, en retrait, et demande une
          confirmation : c'est le seul geste irréversible de cet écran.
        */}
        {editing &&
          (confirmingCancel ? (
            <div className="mt-3 rounded-lg bg-grenat-100 p-3">
              <p className="mb-2 text-sm font-medium text-grenat-700">
                Annuler ce rendez-vous ? Le créneau sera proposé à la liste d&apos;attente.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => cancel.mutate()}
                  className="min-h-10 flex-1 rounded-full bg-grenat-600 text-sm font-medium text-white transition-colors hover:bg-grenat-700 disabled:opacity-50"
                >
                  {cancel.isPending ? "Annulation…" : "Oui, annuler"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(false)}
                  className="min-h-10 flex-1 rounded-full bg-white text-sm font-medium text-encre-700"
                >
                  Revenir
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={pending}
              onClick={() => setConfirmingCancel(true)}
              className="mt-2 min-h-9 w-full rounded-full text-sm text-encre-400 transition-colors hover:bg-creme-100 hover:text-grenat-700"
            >
              Annuler le rendez-vous
            </button>
          ))}
      </div>
    </div>
  );
}

/**
 * Cliente pré-remplie à l'ouverture en modification.
 *
 * Un rendez-vous d'avant la création systématique de fiches ne porte qu'un
 * `guestName` : on le repropose en saisie libre plutôt que d'afficher un
 * champ vide qui ferait croire à une perte de données.
 */
/** Même forme d'étiquette que la recherche, pour que l'écran ne varie pas. */
function prefilledClient(client?: PrefilledClient): ClientChoice | null {
  if (!client) return null;
  return {
    mode: "existing",
    id: client.id,
    label: `${shortName(client.firstName, client.lastName)} · ${client.phone ?? "—"}`,
    allergies: client.allergies ?? null,
  };
}

function initialClient(appointment?: EditableAppointment): ClientChoice | null {
  if (!appointment) return null;

  if (appointment.client) {
    const { id, firstName, lastName, phone, clientProfile } = appointment.client;
    return {
      mode: "existing",
      id,
      label: `${shortName(firstName, lastName)} · ${phone ?? "—"}`,
      allergies: clientProfile?.allergies ?? null,
    };
  }

  if (appointment.guestName) {
    const [first, ...rest] = appointment.guestName.trim().split(/\s+/);
    return { mode: "new", firstName: first ?? "", lastName: rest.join(" "), phone: "" };
  }

  return null;
}

/**
 * `datetime-local` travaille en heure LOCALE du navigateur.
 * Sur le poste du salon les deux coïncident ; on convertit explicitement pour
 * ne pas dépendre de cette coïncidence.
 */
function toLocalInput(date: Date): string {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function fromLocalInput(value: string): Date {
  const naive = new Date(`${value}:00Z`).getTime();
  const probe = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
  }).formatToParts(new Date(naive));
  const get = (type: string) => Number(probe.find((part) => part.type === type)?.value ?? 0);
  const offset = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24) - naive;
  return new Date(naive - offset);
}
